import { useMemo, type FC, type ReactNode } from 'react';
import styled from 'styled-components';

import {
  PageHeader,
  Props as PageHeaderProps,
} from 'src/components/common/page-header';
import { Pagination } from 'src/components/common/pagination';
import { media } from 'src/styles';
import { getLangPath, useSlug } from 'src/utils';

type Props = {
  className?: string;
  children?: ReactNode;
  header: ReactNode | PageHeaderProps;
  pagination?: {
    indexPath?: string | null;
    previousSlug?: string | null;
    nextSlug?: string | null;
  };
};

const usePaginationProps = (pagination: Props['pagination']) => {
  const { lang } = useSlug();
  return useMemo(() => {
    if (!pagination) {
      return null;
    }
    const { previousSlug, nextSlug } = pagination;
    const indexPath = pagination?.indexPath ?? '';
    // NOTE:
    // previousSlugとnextSlugには空白の文字列が入る場合がある
    // （例えば、prevSlugが/news/のindexと同じ場合など）
    // ので、nullとundefinedのみ外れるようにする
    return {
      previousPath:
        previousSlug !== null && previousSlug !== undefined
          ? getLangPath({ path: `${indexPath}${previousSlug}`, lang })
          : undefined,
      indexPath: indexPath
        ? getLangPath({ path: `${indexPath}`, lang })
        : undefined,
      nextPath:
        nextSlug !== null && nextSlug !== undefined
          ? getLangPath({ path: `${indexPath}${nextSlug}`, lang })
          : undefined,
    };
  }, [lang, pagination]);
};

const isPageHeaderProps = (arg: any): arg is PageHeaderProps => 'title' in arg;

export const PageTemplate: FC<Props> = ({
  className,
  children,
  header,
  pagination,
}) => {
  const paginationProps = usePaginationProps(pagination);
  return (
    <Wrapper className={`${className ?? ''}`}>
      {isPageHeaderProps(header) && <PageHeader {...header} />}
      {!isPageHeaderProps(header) && header}
      <Content>{children}</Content>
      {pagination && <StyledPagination {...paginationProps} />}
    </Wrapper>
  );
};

const Content = styled.div`
  text-box-trim: trim-start;
  ${media.lessThanIpadVertical`
    margin-top: ${({ theme }) => theme.structure.margin.block.large}px;
  `}
`;

const StyledPagination = styled(Pagination)`
  margin-top: ${({ theme }) => theme.structure.margin.block.large}px;
`;

const Wrapper = styled.div``;

export default PageTemplate;
